import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { PlusCircle, Trash2, CheckCircle, AlertCircle, ChevronRight } from 'lucide-react';
import logger from '../utils/logger';


// Implementation with backend API support and localStorage fallback
const RoutineBuilder = ({ onComplete }) => {
  const navigate = useNavigate();
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showRoutineDetails, setShowRoutineDetails] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    triggerDescription: '',
    physicalAction: '',
    steps: ['']
  });
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchRoutines();
  }, []);

  const fetchRoutines = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }
      
      try {
        // First try to fetch from API
        const response = await axios.get(`${API_URL}/routines`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setRoutines(response.data);
        // Update localStorage with the latest data
        localStorage.setItem('userRoutines', JSON.stringify(response.data));
      } catch (apiError) {
        logger.error('API fetch failed, using localStorage:', apiError);
        // Fallback to localStorage if API fails
        const savedRoutines = localStorage.getItem('userRoutines');
        if (savedRoutines) {
          setRoutines(JSON.parse(savedRoutines));
        } else {
          setRoutines([]);
        }
      }
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching routines:', error);
      setError('Failed to load routines. Please try again.');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleStepChange = (index, value) => {
    const updatedSteps = [...formData.steps];
    updatedSteps[index] = value;
    setFormData({
      ...formData,
      steps: updatedSteps
    });
  };

  const addStep = () => {
    setFormData({
      ...formData,
      steps: [...formData.steps, '']
    });
  };

  const removeStep = (index) => {
    const updatedSteps = [...formData.steps];
    updatedSteps.splice(index, 1);
    setFormData({
      ...formData,
      steps: updatedSteps
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      triggerDescription: '',
      physicalAction: '',
      steps: ['']
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.name.trim()) {
      setError('Routine name is required');
      return;
    }
    
    if (!formData.triggerDescription.trim()) {
      setError('Trigger description is required');
      return;
    }
    
    if (!formData.physicalAction.trim()) {
      setError('Physical action is required');
      return;
    }

    if (formData.steps.some(step => !step.trim())) {
      setError('All steps must have a description');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      try {
        // Try to save via API first
        const response = await axios.post(`${API_URL}/routines`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Update the routines state with the response from server
        setRoutines([...routines, response.data]);
        
        // Also update localStorage as backup
        const updatedRoutines = [...routines, response.data];
        localStorage.setItem('userRoutines', JSON.stringify(updatedRoutines));
      } catch (apiError) {
        logger.error('API creation failed, using localStorage:', apiError);
        
        // Fallback to localStorage if API fails
        const newRoutine = {
          _id: Date.now().toString(), // Generate a unique ID
          ...formData,
          createdAt: new Date().toISOString()
        };
        
        // Add to current routines
        const updatedRoutines = [...routines, newRoutine];
        
        // Save to localStorage
        localStorage.setItem('userRoutines', JSON.stringify(updatedRoutines));
        setRoutines(updatedRoutines);
      }
      
      setShowForm(false);
      resetForm();
      setError('');
      setSuccess('Routine created successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setLoading(false);
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      logger.error('Error creating routine:', error);
      setError('Failed to create routine. Please try again.');
      setLoading(false);
    }
  };

  const handleViewRoutine = (routine) => {
    setSelectedRoutine(routine);
    setShowRoutineDetails(true);
  };

  const handleDeleteRoutine = async (id) => {
    if (window.confirm('Are you sure you want to delete this routine?')) {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        try {
          // Try to delete via API first
          await axios.delete(`${API_URL}/routines/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (apiError) {
          logger.error('API deletion failed:', apiError);
          // Continue with local deletion even if API fails
        }
        
        // Filter out the routine with the given ID
        const updatedRoutines = routines.filter(routine => routine._id !== id);
        
        // Save to localStorage (as backup or primary if API failed)
        localStorage.setItem('userRoutines', JSON.stringify(updatedRoutines));
        setRoutines(updatedRoutines);
        
        if (selectedRoutine && selectedRoutine._id === id) {
          setShowRoutineDetails(false);
          setSelectedRoutine(null);
        }
        
        setSuccess('Routine deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
        setLoading(false);
      } catch (error) {
        logger.error('Error deleting routine:', error);
        setError('Failed to delete routine. Please try again.');
        setLoading(false);
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="md:flex md:items-center md:justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Performance Routines</h2>
        <button
          onClick={() => {
            setShowForm(true);
            setShowRoutineDetails(false);
            resetForm();
          }}
          className="mt-3 md:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Create New Routine
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {/* Routine Creation Form */}
          {showForm && (
            <div className="col-span-full bg-white shadow overflow-hidden sm:rounded-lg mb-6">
              <div className="px-4 py-5 sm:px-6 bg-gray-50">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Create New Routine</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">Fill out the form to create a new performance routine.</p>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                <form onSubmit={handleSubmit}>
                  <div className="space-y-6">
                    {/* Routine Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Routine Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="e.g., Pre-Shot Basketball Routine"
                      />
                    </div>

                    {/* Trigger Description */}
                    <div>
                      <label htmlFor="triggerDescription" className="block text-sm font-medium text-gray-700">
                        When do you use this routine? (Describe the Trigger)
                      </label>
                      <textarea
                        id="triggerDescription"
                        name="triggerDescription"
                        rows="3"
                        value={formData.triggerDescription}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="e.g., When I feel nervous before taking a free throw"
                      ></textarea>
                    </div>

                    {/* Physical Action */}
                    <div>
                      <label htmlFor="physicalAction" className="block text-sm font-medium text-gray-700">
                        Physical Action
                      </label>
                      <textarea
                        id="physicalAction"
                        name="physicalAction"
                        rows="3"
                        value={formData.physicalAction}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="e.g., Bounce the ball three times, spin it in my hands"
                      ></textarea>
                    </div>

                    {/* Steps to Return to Present */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Steps to Return to the Present Moment
                      </label>
                      
                      {formData.steps.map((step, index) => (
                        <div key={index} className="flex mb-3">
                          <div className="flex-grow">
                            <div className="flex items-center">
                              <span className="text-gray-500 font-medium mr-2">{index + 1}.</span>
                              <input
                                type="text"
                                value={step}
                                onChange={(e) => handleStepChange(index, e.target.value)}
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                placeholder={`e.g., Step ${index + 1}: Focus on my target`}
                              />
                            </div>
                          </div>
                          {formData.steps.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeStep(index)}
                              className="ml-2 flex-shrink-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={20} />
                            </button>
                          )}
                        </div>
                      ))}
                      
                      <button
                        type="button"
                        onClick={addStep}
                        className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <PlusCircle size={16} className="mr-2" />
                        Add Another Step
                      </button>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(false);
                          resetForm();
                        }}
                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Save Routine
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Routine Details View */}
          {showRoutineDetails && selectedRoutine && (
            <div className="col-span-full bg-white shadow overflow-hidden sm:rounded-lg mb-6">
              <div className="px-4 py-5 sm:px-6 bg-gray-50 flex justify-between items-center">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">{selectedRoutine.name}</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">Performance routine details</p>
                </div>
                <button
                  onClick={() => setShowRoutineDetails(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="border-t border-gray-200">
                <dl>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Trigger</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{selectedRoutine.triggerDescription}</dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500"></dt>
                    <dd className="mt-1 text-sm font-medium text-indigo-600 sm:mt-0 sm:col-span-2">Take a Breath</dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Physical Action</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{selectedRoutine.physicalAction}</dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500"></dt>
                    <dd className="mt-1 text-sm font-medium text-indigo-600 sm:mt-0 sm:col-span-2">Take Another Breath</dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Return to Present Steps</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                        {selectedRoutine.steps.map((step, index) => (
                          <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                            <div className="w-0 flex-1 flex items-center">
                              <span className="flex-shrink-0 h-5 w-5 text-gray-400">{index + 1}.</span>
                              <span className="ml-2 flex-1 w-0 truncate">{step}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                </dl>
              </div>
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  onClick={() => handleDeleteRoutine(selectedRoutine._id)}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete Routine
                </button>
              </div>
            </div>
          )}

          {/* Routines List */}
          {!showForm && !showRoutineDetails && (
            routines.length === 0 ? (
              <div className="col-span-full text-center py-10 bg-white shadow overflow-hidden sm:rounded-lg">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No routines</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by creating your first performance routine.</p>
                <div className="mt-6">
                  <button
                    onClick={() => {
                      setShowForm(true);
                      setShowRoutineDetails(false);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <PlusCircle className="mr-2 h-5 w-5" />
                    Create Routine
                  </button>
                </div>
              </div>
            ) : (
              <>
                {routines.map(routine => (
                  <div
                    key={routine._id}
                    className="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200 hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="px-4 py-5 sm:px-6">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 truncate">{routine.name}</h3>
                    </div>
                    <div className="px-4 py-4 sm:p-6">
                      <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                        <span className="font-medium">Trigger:</span> {routine.triggerDescription}
                      </p>
                      <p className="text-sm text-gray-500 line-clamp-2">
                        <span className="font-medium">Steps:</span> {routine.steps.length}
                      </p>
                    </div>
                    <div className="px-4 py-4 sm:px-6 flex justify-end">
                      <button
                        onClick={() => handleViewRoutine(routine)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        View Details
                        <ChevronRight size={16} className="ml-1" />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default RoutineBuilder;